import { Suspense, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Components } from 'react-virtuoso'
import { Virtuoso } from 'react-virtuoso'

import { paths } from 'src/config/paths'
import Post from 'src/features/feeds/components/posts/post'
import { PostSkeleton } from 'src/features/feeds/components/posts/post-skeleton'
import { SCROLL_VELOCITY_THRESHOLD } from 'src/lib/constants'
import { ErrorBoundaryWrapper } from 'src/shared/components/errors/error-boundary-wrapper'
import { Button } from 'src/shared/components/ui/buttons/button'
import { Spinner } from 'src/shared/components/ui/miscellaneous/spinner'
import { useUserFeed } from 'src/shared/features/feed/api/use-user-feed'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import pencilImage from 'src/shared/features/users/assets/pencil.png'
import { useIsMobile } from 'src/shared/hooks/use-mobile'
import { cn } from 'src/shared/utils/cn'
const ScrollSeekPlaceholder: Components['ScrollSeekPlaceholder'] = ({ height }) => (
  <div style={{ height }} className="py-2">
    <PostSkeleton />
  </div>
)

interface PostSectionProps {
  userId: number
  isPublic?: boolean
}

const PostSectionContent: React.FC<PostSectionProps> = ({ userId, isPublic = false }) => {
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const { data: currentUser } = useMyUserData()
  const { data: feedData, fetchNextPage, hasNextPage, isFetchingNextPage } = useUserFeed(userId, isPublic)

  // Flatten posts data for virtualization
  const posts = useMemo(() => {
    return feedData?.pages.flatMap((page) => page.results) || []
  }, [feedData])

  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }

  if (!feedData?.pages[0]?.results.length) {
    const isCurrentUser = currentUser?.id === userId
    return (
      <div className="mt-8 flex w-full flex-col items-center justify-center rounded-lg border-2 border-dashed bg-accent p-5 text-center text-muted">
        <img src={pencilImage} alt="No posts" className="mb-4 size-12" />
        {isCurrentUser ? (
          <>
            <p className="mb-2">You haven&apos;t posted anything</p>
            <Button onClick={() => navigate(paths.feed.path)} variant="link" className="text-primary underline">
              Post now
            </Button>
          </>
        ) : (
          <p>This user hasn&apos;t posted anything yet</p>
        )}
      </div>
    )
  }

  // Custom footer component
  const Footer = () => {
    if (isFetchingNextPage) {
      return (
        <div className="flex h-20 w-full justify-center">
          <Spinner />
        </div>
      )
    }
    return <div className="h-16 bg-white" /> // Add extra space at the bottom to prevent footer overlap
  }

  return (
    <div className={cn('mt-8 w-full', isMobile ? 'bg-accent' : '')}>
      <Virtuoso
        data={posts}
        endReached={loadMore}
        overscan={200}
        useWindowScroll={true}
        itemContent={(index, post) => (
          <div className="py-1">
            <Post key={post.id} post={post} variant={isMobile ? 'list' : 'card'} isPublic={isPublic} />
          </div>
        )}
        components={{
          Footer,
          ScrollSeekPlaceholder,
        }}
        scrollSeekConfiguration={{
          enter: (velocity) => Math.abs(velocity) > SCROLL_VELOCITY_THRESHOLD,
          exit: (velocity) => Math.abs(velocity) < 10,
        }}
        style={{ height: '100%', width: '100%', scrollbarWidth: 'none' }}
      />
    </div>
  )
}

export const PostSection: React.FC<PostSectionProps> = ({ userId, isPublic = false }) => {
  return (
    <ErrorBoundaryWrapper
      title="Error loading posts"
      description="There was an error loading this user's posts. Please try again later."
    >
      <Suspense
        fallback={
          <div className="mt-8 w-full space-y-4">
            <PostSkeleton />
            <PostSkeleton />
            <PostSkeleton />
          </div>
        }
      >
        <PostSectionContent userId={userId} isPublic={isPublic} />
      </Suspense>
    </ErrorBoundaryWrapper>
  )
}
